import { Button, Icon, LoadingAnimation, Modal, Text } from '@/components'
import React, { useState } from 'react'
import { ID } from '@/types/model'
import {
  DeviceIdSelections,
  EnrollableDeviceChecklist,
} from '../EnrollableDeviceChecklist/EnrollableDeviceChecklist'
import { AlertMessage, ConfigurableText } from 'app-components'
import { match } from 'ts-pattern'
import useEnrollmentActions from '@/hooks/useEnrollmentActions'
import { logEvent } from '@/logging'
import { useAppDispatch } from '@/hooks'
import { invalidateCollections } from '@/app/pages/multi-program-enrollment/utils/utils'
import useEnrollableDevices from '@/app/hooks/useEnrollableDevices'

export function getDeviceSelectionCount(
  selections: DeviceIdSelections | undefined,
) {
  if (!selections) {
    return 0
  }
  return (
    selections.chargerIds.length +
    selections.profileIds.length +
    selections.thermostatIds.length +
    selections.vehicleIds.length
  )
}

export function AdditionalEnrollmentModal(props: {
  open: boolean
  programId: ID
  onClose: () => void
}) {
  const [enrollmentSelections, setEnrollmentSelections] =
    useState<DeviceIdSelections>()
  const dispatch = useAppDispatch()
  const [error, setError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    enrollableVehicles,
    enrollableThermostats,
    enrollableChargers,
    enrollableProfiles,
  } = useEnrollableDevices(props.programId)

  const { enrollDevices, logRequestProgramEnrollment } = useEnrollmentActions()

  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false)

  const onClose = () => {
    if (isSubmitting) {
      return
    }

    if (enrollmentSuccess) {
      invalidateCollections(dispatch)
    }
    props.onClose()
  }

  const onSubmit = async () => {
    if (!enrollmentSelections) {
      return
    }

    setIsSubmitting(true)

    logRequestProgramEnrollment({
      vehicles: enrollableVehicles,
      chargers: enrollableChargers,
      thermostats: enrollableThermostats,
      selections: enrollmentSelections,
      utilityProgramId: props.programId,
    })
    const response = await enrollDevices({
      utilityProgramId: props.programId,
      vehicleIds: enrollmentSelections?.vehicleIds ?? [],
      thermostatIds: enrollmentSelections?.thermostatIds ?? [],
      chargerIds: enrollmentSelections?.chargerIds ?? [],
      skipInvalidation: true,
    })

    if (response.isErr()) {
      logEvent(`Error enrolling profile`)
      setError(
        'There was an error enrolling these devices. Please try again, or make a different selection.',
      )
      setIsSubmitting(false)
      return
    }

    // Success!
    setIsSubmitting(false)
    setEnrollmentSuccess(true)
  }

  const selectionCount = getDeviceSelectionCount(enrollmentSelections)

  const submissionText = (): string => {
    if (selectionCount === 0) {
      return 'Select Devices to Enroll'
    }

    if (selectionCount === 1) {
      return 'Yes, Enroll (1) Device'
    } else {
      return `Yes, Enroll (${selectionCount}) Devices`
    }
  }

  return (
    <Modal
      open={props.open}
      id="additional-enrollments"
      title={enrollmentSuccess ? '' : 'Want to enroll additional devices?'}
      onClose={onClose}
    >
      {error && <AlertMessage variant="error">{error}</AlertMessage>}
      {match(enrollmentSuccess)
        .with(true, () => {
          return <SuccessScreen onClose={onClose} />
        })
        .with(false, () => {
          return (
            <div className="flex flex-col gap-4 mt-2">
              {match(isSubmitting)
                .with(true, () => {
                  return (
                    <div className="flex flex-col gap-4 items-center">
                      <LoadingAnimation type="falling" centered />
                      <Text variant="body2">
                        Enrolling your devices, please wait...
                      </Text>
                    </div>
                  )
                })
                .with(false, () => {
                  return (
                    <EnrollableDeviceChecklist
                      programId={props.programId}
                      onValuesChanged={setEnrollmentSelections}
                    />
                  )
                })
                .exhaustive()}
              <Button
                id="submit"
                variant="primary"
                onClick={onSubmit}
                disabled={selectionCount === 0 || isSubmitting}
              >
                {submissionText()}
              </Button>
              <Button
                id="cancel"
                variant="primary-subtle"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          )
        })
        .exhaustive()}
    </Modal>
  )
}

function SuccessScreen(props: { onClose: () => void }) {
  return (
    <div className="flex flex-col align-content-center items-center gap-4">
      <Icon name="Check" size={50} />
      <ConfigurableText variant="h3" className="text-center">
        Your additional device(s) have been successfully enrolled!
      </ConfigurableText>
      <Button id="continue" onClick={props.onClose} variant="primary">
        Continue
      </Button>
    </div>
  )
}
